
import { format, isValid, parseISO, parse } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { formatTimeString } from "./formatTime";

/**
 * Format a date string consistently with proper timezone handling
 * @param dateString ISO date string from the database 
 * @param formatString Optional date-fns format string (defaults to "MMMM d, yyyy")
 * @returns Formatted date string or empty string if invalid
 */
export const formatDate = (dateString?: string | null, formatString: string = "MMMM d, yyyy"): string => {
  if (!dateString) return '';

  try {
    // Check if the dateString is a simple YYYY-MM-DD format without time
    const isSimpleDate = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
    
    if (isSimpleDate) {
      // For simple YYYY-MM-DD formats, parse directly using parse() instead of parseISO()
      // This avoids timezone interpretation issues completely
      const [year, month, day] = dateString.split('-').map(Number);
      
      // Month is 0-indexed in JavaScript Date
      const date = new Date(year, month - 1, day);
      
      console.log('Simple date detected, parsed as:', date);
      console.log('Using direct date components:', { year, month, day });
      
      return format(date, formatString);
    }
    
    // For ISO dates with time components, use parseISO
    const date = parseISO(dateString);
    
    // Check if the date is valid
    if (!isValid(date)) return '';
    
    console.log('Complex date detected:', dateString);
    console.log('Parsed with parseISO:', date);
    
    // Use formatInTimeZone to ensure we respect the original timezone
    return formatInTimeZone(date, 'UTC', formatString);
  } catch (error) {
    console.error("Error formatting date:", error);
    return '';
  }
};

/**
 * Format a date and time together with proper timezone handling
 * @param dateString ISO date string
 * @param timeString Time string (can be in various formats)
 * @param dateFormatString Optional date-fns format string
 * @returns Formatted date and time string
 */
export const formatDateAndTime = (
  dateString?: string | null, 
  timeString?: string | null, 
  dateFormatString: string = "MMMM d, yyyy"
): string => {
  if (!dateString) return timeString ? formatTimeString(timeString) : '';
  
  const formattedDate = formatDate(dateString, dateFormatString);
  
  if (!formattedDate) return '';
  if (!timeString) return formattedDate;
  
  return `${formattedDate} at ${formatTimeString(timeString)}`;
};

/**
 * Format a date string with a short date format (MM/dd/yyyy)
 * @param dateString ISO date string
 * @returns Formatted short date string
 */
export const formatShortDate = (dateString?: string | null): string => {
  return formatDate(dateString, "MM/dd/yyyy");
};
